import React, { useId } from 'react';
import './Table.css';

export interface Column<T> {
    key: string;
    header: string;
    render?: (row: T) => React.ReactNode;
    /** Render this column's cells as <th scope="row"> (the identifying column). */
    rowHeader?: boolean;
}

export interface TableProps<T> {
    columns: Column<T>[];
    data: T[];
    isLoading?: boolean;
    emptyMessage?: string;
    /** Names the table and its scroll region. Strongly recommended. */
    caption?: React.ReactNode;
    /** Names the scroll region when there is no caption (defaults to "Table"). */
    label?: string;
    /** Keep the caption for assistive tech only. */
    hideCaption?: boolean;
    /** Header row sticks while the region scrolls (the region gets a max height). */
    stickyHeader?: boolean;
    density?: 'comfortable' | 'compact';
    rowKey?: (row: T, index: number) => React.Key;
}

/**
 * Semantic data table: <caption>, th scope, and a contained scroll region so a
 * wide table scrolls inside itself instead of the page (spec §5, §13).
 */
export function Table<T extends Record<string, unknown>>({
    columns, data, isLoading, emptyMessage = 'No data found',
    caption, label, hideCaption, stickyHeader, density = 'comfortable', rowKey,
}: TableProps<T>) {
    const captionId = useId();
    if (isLoading) return <div className="table-loading"><div className="spinner spinner-md" aria-label="Loading" /></div>;

    const labelled = caption !== undefined;
    return (
        <div
            className={`table-wrapper ui-table-scroll ${stickyHeader ? 'ui-table-scroll--sticky' : ''}`}
            role="region"
            tabIndex={0}
            {...(labelled ? { 'aria-labelledby': captionId } : { 'aria-label': label ?? 'Table' })}
        >
            <table className={`table ui-table ui-table--${density}`}>
                {labelled && <caption id={captionId} className={hideCaption ? 'sr-only' : 'ui-table-caption'}>{caption}</caption>}
                <thead>
                    <tr>
                        {columns.map((col) => <th key={col.key} scope="col">{col.header}</th>)}
                    </tr>
                </thead>
                <tbody>
                    {data.length === 0 ? (
                        <tr><td colSpan={columns.length} className="table-empty">{emptyMessage}</td></tr>
                    ) : (
                        data.map((row, i) => (
                            <tr key={rowKey ? rowKey(row, i) : i}>
                                {columns.map((col) => {
                                    const content = col.render ? col.render(row) : String(row[col.key] ?? '');
                                    return col.rowHeader
                                        ? <th key={col.key} scope="row">{content}</th>
                                        : <td key={col.key}>{content}</td>;
                                })}
                            </tr>
                        ))
                    )}
                </tbody>
            </table>
        </div>
    );
}
